import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  product_interest: "hardware",
  destination_country: "",
  target_budget: "",
  message: "",
};

export default function QuoteForm({ defaultProduct }) {
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const cataloguePage = searchParams.get("page");
  const fromCatalogue = searchParams.get("from") === "catalogue";

  const [form, setForm] = useState(() => {
    const base = {
      ...initialState,
      product_interest: defaultProduct || "hardware",
    };
    if (fromCatalogue && cataloguePage) {
      base.product_interest = "hardware";
      base.message = `I'm interested in catalogue page ${cataloguePage}. Please send details and pricing.`;
    }
    return base;
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // If URL params change while mounted, resync message
  useEffect(() => {
    if (fromCatalogue && cataloguePage) {
      setForm((prev) => ({
        ...prev,
        product_interest: "hardware",
        message: prev.message
          ? prev.message
          : `I'm interested in catalogue page ${cataloguePage}. Please send details and pricing.`,
      }));
    }
  }, [fromCatalogue, cataloguePage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      const payload = { ...form };
      // strip empty strings for optional fields
      if (!payload.contact_name) delete payload.contact_name;
      if (!payload.phone) delete payload.phone;
      if (!payload.target_budget) delete payload.target_budget;
      if (!payload.message) delete payload.message;

      const res = await axios.post(`${API}/quotes`, payload);
      if (res.data?.ok) {
        setStatus("success");
        setForm({ ...initialState, product_interest: defaultProduct || "hardware" });
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || t.common.errorMsg
      );
    }
  };

  if (status === "success") {
    return (
      <div
        className="bg-white border border-[#E2E8F0] p-10 flex flex-col items-start gap-4"
        data-testid="quote-success"
      >
        <CheckCircle2 size={40} className="text-[#1D4ED8]" />
        <h3 className="text-2xl font-black tracking-tight text-[#0B1B29]">
          {t.common.successTitle}
        </h3>
        <p className="text-[#536878] max-w-md">{t.common.successMsg}</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="btn-secondary mt-2"
          data-testid="quote-success-new"
        >
          {t.common.requestQuote} <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-[#E2E8F0] p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
      data-testid="quote-form"
      noValidate
    >
      <div>
        <label className="pgb-label" htmlFor="company_name">{t.quotePage.form.company} *</label>
        <input
          id="company_name"
          name="company_name"
          required
          value={form.company_name}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-company"
        />
      </div>

      <div>
        <label className="pgb-label" htmlFor="contact_name">{t.quotePage.form.contact}</label>
        <input
          id="contact_name"
          name="contact_name"
          value={form.contact_name}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-contact"
        />
      </div>

      <div>
        <label className="pgb-label" htmlFor="email">{t.quotePage.form.email} *</label>
        <input
          id="email"
          name="email"
          type="email"
          required
          value={form.email}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-email"
        />
      </div>

      <div>
        <label className="pgb-label" htmlFor="phone">{t.quotePage.form.phone}</label>
        <input
          id="phone"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-phone"
        />
      </div>

      <div>
        <label className="pgb-label" htmlFor="product_interest">{t.quotePage.form.product} *</label>
        <select
          id="product_interest"
          name="product_interest"
          value={form.product_interest}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-product"
        >
          <option value="hardware">{t.quotePage.form.products.hardware}</option>
          <option value="furniture">{t.quotePage.form.products.furniture}</option>
          <option value="logistics">{t.quotePage.form.products.logistics}</option>
          <option value="all">{t.quotePage.form.products.all}</option>
        </select>
      </div>

      <div>
        <label className="pgb-label" htmlFor="destination_country">{t.quotePage.form.destination} *</label>
        <input
          id="destination_country"
          name="destination_country"
          required
          value={form.destination_country}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-destination"
        />
      </div>

      <div className="md:col-span-2">
        <label className="pgb-label" htmlFor="target_budget">{t.quotePage.form.targetBudget}</label>
        <input
          id="target_budget"
          name="target_budget"
          placeholder={t.quotePage.form.targetBudgetPlaceholder}
          value={form.target_budget}
          onChange={handleChange}
          className="pgb-input"
          data-testid="quote-input-budget"
        />
      </div>

      <div className="md:col-span-2">
        <label className="pgb-label" htmlFor="message">{t.quotePage.form.message}</label>
        <textarea
          id="message"
          name="message"
          rows={5}
          value={form.message}
          onChange={handleChange}
          className="pgb-input resize-none"
          data-testid="quote-input-message"
        />
      </div>

      <div className="md:col-span-2 flex flex-col md:flex-row md:items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="quote-submit-btn"
        >
          {status === "loading" ? (
            <>
              <Loader2 size={16} className="animate-spin" /> {t.common.submitting}
            </>
          ) : (
            <>
              {t.common.submit} <ArrowRight size={16} />
            </>
          )}
        </button>

        {status === "error" && (
          <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="quote-error">
            <AlertCircle size={16} /> {errorMsg}
          </div>
        )}
      </div>
    </form>
  );
}
